/**
 * How much of a route runs on a big road.
 *
 * The driver's requirement: there should ALWAYS be an option that stays on big
 * roads - two lanes each way or better - for all or most of the journey, even
 * when it is not the quickest. In a loaded truck that is not a preference: it
 * is the difference between overtaking safely and four hours behind a tractor
 * on a road with no shoulder.
 *
 * What the data can and cannot tell us
 * ------------------------------------
 * Mapbox has no "prefer major roads" parameter, so this cannot be requested -
 * only recognised among candidates. That leaves the question of how to
 * recognise it, and the honest answer for Thailand is narrower than it first
 * appears. Checked against live responses in Isaan:
 *
 *  - `maxspeed` annotations are 100% "unknown" here. Useless.
 *  - `intersections[].lanes` is present on anywhere between 3% and 84% of the
 *    intersections of a route, and it describes TURN lanes at a junction - a
 *    two-lane road shows three at a junction with a right-turn pocket. It
 *    cannot be read as carriageway width.
 *  - `mapbox_streets_v8.class` is complete and consistent.
 *
 * So this measures ROAD CLASS, and the naming here says so. `trunk` and
 * `motorway` in Thailand are the numbered national highways, which are divided
 * carriageways almost without exception; `primary` is a provincial highway,
 * usually wide but not guaranteed; `secondary` and below routinely means one
 * lane each way despite sounding substantial. An earlier version of this file
 * counted `secondary` as dual carriageway and reported "100% dual" for every
 * candidate including the one that avoided every highway - a number that was
 * both useless for ranking and actively misleading to read.
 *
 * Two figures matter, not one:
 *  - the SHARE of the trip on big roads, which is what "most of the way" means;
 *  - the LONGEST continuous run off them, because 90% big with a 40 km village
 *    section is not the route being asked for, and an average hides exactly
 *    that.
 */

import { bearing, bearingDelta, destinationPoint, distance } from '../core/geo';
import type { LngLat, Route, RouteStep } from '../core/types';

/**
 * Classes that are genuinely big roads here. `trunk` and `motorway` are the
 * national highway network; `primary` is the provincial one and is included
 * because excluding it would reject perfectly good four-lane routes.
 */
const BIG_CLASSES = new Set([
  'motorway',
  'motorway_link',
  'trunk',
  'trunk_link',
  'primary',
  'primary_link',
]);

/**
 * Genuine motorways only - the 120 km/h grade-separated roads.
 *
 * Kept separate from the rest of the big roads because they are a different
 * drive, not merely a faster one: no traffic lights, no U-turn gaps, nothing
 * pulling out of a soi. A route that is 60% motorway and a route that is 60%
 * trunk road are not comparable, and reporting them under one figure hid that.
 */
const MOTORWAY_CLASSES = new Set(['motorway', 'motorway_link']);

/** Dual carriageway: 2+ lanes each way, but an ordinary road with junctions. */
const DUAL_CLASSES = new Set(['trunk', 'trunk_link', 'primary', 'primary_link']);
/** Mainline classes that normally carry 80-120 km/h limits. */
const FAST_MAINLINE_CLASSES = new Set(['motorway', 'trunk', 'primary']);

export interface RoadWidthProfile {
  /** Share on dual carriageway or better, 0..1. */
  bigFraction: number;
  /** Share on grade-separated 120 km/h motorway, 0..1. */
  motorwayFraction: number;
  /** Share on dual carriageway that is NOT motorway, 0..1. */
  dualFraction: number;
  /** Metres on roads below dual carriageway. */
  smallMetres: number;
  /** Longest unbroken run on roads below dual carriageway, metres. */
  worstSmallRun: number;
}

/** At or above this share, the route counts as big roads "most of the way". */
export const MOSTLY_BIG = 0.8;
/**
 * A run off the big roads longer than this disqualifies a route however good
 * its average is - roughly fifteen minutes of no overtaking.
 */
export const MAX_SMALL_RUN_M = 20_000;

/** Short links between big roads are not a detour onto small roads. */
const IGNORE_LINK_M = 300;

/** Dominant Mapbox street class over a step. */
function stepClass(step: RouteStep): string {
  const tally = new Map<string, number>();
  for (const x of step.intersections ?? []) {
    const c = x.mapbox_streets_v8?.class;
    if (c) tally.set(c, (tally.get(c) ?? 0) + 1);
  }
  let best = '';
  let top = 0;
  for (const [c, n] of tally) {
    if (n > top) {
      top = n;
      best = c;
    }
  }
  return best;
}

/** True only for a grade-separated motorway, not an ordinary trunk road. */
export function isMotorwayStep(step: RouteStep): boolean {
  return MOTORWAY_CLASSES.has(stepClass(step));
}

/**
 * A silent routing point just beyond the destination's projection on the last
 * motorway.
 *
 * Directions engines normally optimise only the obvious early exit. That
 * means they may never compare it with staying on the elevated road to the
 * next exit and returning, even when the latter avoids a slow surface-road
 * crawl. We use the final mainline motorway step as the corridor direction,
 * project the goal onto that direction, then look roughly one exit farther.
 *
 * This is deliberately bounded: it only applies when the goal is close to the
 * motorway and never sends the probe more than 8 km beyond the route's exit.
 * The caller still checks the resulting route's live ETA and actual motorway
 * time before offering it.
 */
export function fastRoadBeyondDestinationProbe(
  route: Route,
  destination: LngLat,
): LngLat | null {
  const steps = route.legs.flatMap((leg) => leg.steps);
  const reversed = [...steps].reverse();
  const motorwayMainline = reversed.find(
    (step) => stepClass(step) === 'motorway' && (step.geometry?.coordinates.length ?? 0) >= 2,
  );
  const fastMainline = reversed.find(
    (step) =>
      FAST_MAINLINE_CLASSES.has(stepClass(step)) && (step.geometry?.coordinates.length ?? 0) >= 2,
  );
  // Elevated/motorway gets first refusal. Only fall back to trunk/primary when
  // there is no meaningful motorway corridor in this route at all.
  const mainline = motorwaySeconds(route) >= 120 ? motorwayMainline : fastMainline;
  if (!mainline?.geometry || fastRoadSeconds(route) < 120) return null;

  const coords = mainline.geometry.coordinates;
  const exit = coords[coords.length - 1];
  let approach = coords[coords.length - 2];
  for (let i = coords.length - 2; i >= 0; i--) {
    approach = coords[i];
    if (distance(approach, exit) >= 500) break;
  }

  const roadBearing = bearing(approach, exit);
  const goalDistance = distance(exit, destination);
  if (goalDistance > 12_000) return null;

  const goalDelta = bearingDelta(roadBearing, bearing(exit, destination));
  const goalDeltaRad = (goalDelta * Math.PI) / 180;
  const alongRoad = goalDistance * Math.cos(goalDeltaRad);
  const acrossRoad = Math.abs(goalDistance * Math.sin(goalDeltaRad));

  // If the goal is behind the chosen exit, this route already went beyond it.
  // A goal far off the corridor is not a next-exit problem either.
  if (alongRoad < 250 || acrossRoad > 8_000) return null;

  const probeDistance = Math.min(8_000, Math.max(2_500, alongRoad + 2_500));
  return destinationPoint(exit, roadBearing, probeDistance);
}

/**
 * A short silent point behind the departure direction.
 *
 * This makes the router compare the non-obvious move of initially driving
 * away from the goal to reach a nearby expressway ramp. It is only useful on
 * a substantial trip; on a local journey it would be needless churn.
 */
export function fastRoadAccessProbe(origin: LngLat, destination: LngLat): LngLat | null {
  const tripDistance = distance(origin, destination);
  // Very short local hops still do not justify a forced loop, but from 8 km
  // onward the nearby expressway can be the calmer route even when its ramp
  // begins behind the car.
  if (tripDistance < 8_000) return null;
  const backtrack = Math.min(3_000, Math.max(1_500, tripDistance * 0.025));
  return destinationPoint(origin, (bearing(origin, destination) + 180) % 360, backtrack);
}

export function roadWidthProfile(route: Route): RoadWidthProfile {
  const steps: RouteStep[] = route.legs.flatMap((l) => l.steps);
  const total = Math.max(1, route.distance);

  let big = 0;
  let motorway = 0;
  let dual = 0;
  let small = 0;
  let run = 0;
  let worstRun = 0;

  for (const step of steps) {
    const cls = stepClass(step);
    const d = step.distance;
    const isBig = BIG_CLASSES.has(cls);

    if (isBig) {
      big += d;
      if (MOTORWAY_CLASSES.has(cls)) motorway += d;
      else if (DUAL_CLASSES.has(cls)) dual += d;
      // Only a decent stretch of big road actually breaks a bad run; a 150 m
      // slip road between two village sections does not.
      if (d > IGNORE_LINK_M) run = 0;
    } else {
      small += d;
      run += d;
      if (run > worstRun) worstRun = run;
    }
  }

  return {
    bigFraction: big / total,
    motorwayFraction: motorway / total,
    dualFraction: dual / total,
    smallMetres: Math.round(small),
    worstSmallRun: Math.round(worstRun),
  };
}

/**
 * Seconds from departure until the route first runs on motorway class.
 *
 * In Bangkok the motorway class IS the elevated network - the tollways and
 * expressways stacked over the surface arteries. "Get me up there as soon as
 * possible" is a question about this number, not about the total motorway
 * share: a route can be 60% motorway and still spend its first 25 minutes
 * crawling Rama IV to reach the ramp.
 */
export function secondsToMotorway(route: Route): number {
  let t = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (MOTORWAY_CLASSES.has(stepClass(step))) return t;
      t += step.duration;
    }
  }
  return Infinity;
}

/** Seconds from departure until a motorway, trunk, or primary road. */
export function secondsToFastRoad(route: Route): number {
  let t = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (BIG_CLASSES.has(stepClass(step))) return t;
      t += step.duration;
    }
  }
  return Infinity;
}

/** Seconds of the route spent ON motorway class. */
export function motorwaySeconds(route: Route): number {
  let t = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (MOTORWAY_CLASSES.has(stepClass(step))) t += step.duration;
    }
  }
  return t;
}

/** Metres on the expressway mainline, excluding entry and exit ramps. */
function motorwayMainlineMetres(route: Route): number {
  let metres = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (stepClass(step) === 'motorway') metres += step.distance;
    }
  }
  return metres;
}

/**
 * Geometry around the genuine expressway corridor in a route.
 *
 * `motorway_link` is part of the access-controlled network when it directly
 * joins a motorway mainline, but a ramp by itself never qualifies a route as
 * expressway. Distances before the connected entry ramp and after the connected
 * exit ramp are deliberately kept separate: together they express "get on
 * promptly and stay on until the closest practical exit" without trusting an
 * ETA model for Bangkok surface traffic.
 */
export interface ExpresswayGeometryProfile {
  /** Route distance before the entry ramp connected to the first mainline. */
  accessMetres: number;
  /** Route distance after the exit ramp connected to the last mainline. */
  tailMetres: number;
  /** All non-expressway distance between the first and last mainline. */
  internalSurfaceMetres: number;
  /** Internal surface gaps large enough to be real exits, not map seams. */
  disconnectedSurfaceMetres: number;
  /** Surface distance at the two ends of the expressway corridor. */
  edgeSurfaceMetres: number;
  /** Mainline distance in the longest connected expressway run. */
  longestConnectedMainlineMetres: number;
}

/**
 * Pure geometry profile for initial expressway selection.
 *
 * A short unclassified step can occur at a toll gate or a Mapbox class seam.
 * Reuse the existing 300 m link tolerance so that does not split one physical
 * expressway. Longer gaps are genuine leave-and-rejoin sections and lead the
 * route comparison ahead of every other measurement.
 */
export function expresswayGeometryProfile(route: Route): ExpresswayGeometryProfile | null {
  const steps = route.legs.flatMap((leg) => leg.steps);
  const classes = steps.map(stepClass);
  const firstMainline = classes.indexOf('motorway');
  if (firstMainline < 0) return null;

  let lastMainline = firstMainline;
  for (let i = firstMainline + 1; i < classes.length; i++) {
    if (classes[i] === 'motorway') lastMainline = i;
  }

  let entryAt = firstMainline;
  while (entryAt > 0 && classes[entryAt - 1] === 'motorway_link') entryAt--;
  let exitAt = lastMainline;
  while (exitAt + 1 < classes.length && classes[exitAt + 1] === 'motorway_link') exitAt++;

  const metres = (from: number, to: number): number => {
    let total = 0;
    for (let i = from; i < to; i++) total += Math.max(0, steps[i]?.distance ?? 0);
    return total;
  };

  const accessMetres = metres(0, entryAt);
  const tailMetres = metres(exitAt + 1, steps.length);
  let internalSurfaceMetres = 0;
  let disconnectedSurfaceMetres = 0;
  let pendingGapMetres = 0;
  let connectedMainlineMetres = 0;
  let longestConnectedMainlineMetres = 0;

  const finishGap = (): void => {
    if (pendingGapMetres > IGNORE_LINK_M) {
      disconnectedSurfaceMetres += pendingGapMetres;
      connectedMainlineMetres = 0;
    }
    pendingGapMetres = 0;
  };

  for (let i = entryAt; i <= exitAt; i++) {
    const cls = classes[i];
    const distanceMetres = Math.max(0, steps[i]?.distance ?? 0);
    if (MOTORWAY_CLASSES.has(cls)) {
      finishGap();
      if (cls === 'motorway') {
        connectedMainlineMetres += distanceMetres;
        longestConnectedMainlineMetres = Math.max(
          longestConnectedMainlineMetres,
          connectedMainlineMetres,
        );
      }
    } else {
      internalSurfaceMetres += distanceMetres;
      pendingGapMetres += distanceMetres;
    }
  }
  finishGap();

  return {
    accessMetres,
    tailMetres,
    internalSurfaceMetres,
    disconnectedSurfaceMetres,
    edgeSurfaceMetres: accessMetres + tailMetres,
    longestConnectedMainlineMetres,
  };
}

/**
 * Expressway routes that remain reasonable alternatives to the ordinary
 * shortest route. Keeping this test in one place makes the route list
 * and the default-route preference agree about what "expressway available"
 * means: genuine motorway mainline within the accepted 15 km distance guard.
 * ETA is intentionally absent: Bangkok surface-road estimates have proved too
 * unreliable to decide whether an expressway corridor is useful.
 */
export function viableExpresswayRoutes(routes: Route[]): Route[] {
  if (!routes.length) return [];
  const shortestDistance = Math.min(...routes.map((route) => route.distance));
  return routes.filter(
    (route) =>
      motorwayMainlineMetres(route) > 0 &&
      route.distance <= shortestDistance + FAST_ROAD_EXTRA_DISTANCE_M,
  );
}

function expresswayGeometryWins(
  route: Route,
  profile: ExpresswayGeometryProfile,
  currentRoute: Route,
  current: ExpresswayGeometryProfile,
): boolean {
  if (profile.disconnectedSurfaceMetres !== current.disconnectedSurfaceMetres) {
    return profile.disconnectedSurfaceMetres < current.disconnectedSurfaceMetres;
  }
  if (profile.edgeSurfaceMetres !== current.edgeSurfaceMetres) {
    return profile.edgeSurfaceMetres < current.edgeSurfaceMetres;
  }
  if (profile.tailMetres !== current.tailMetres) {
    return profile.tailMetres < current.tailMetres;
  }
  if (profile.accessMetres !== current.accessMetres) {
    return profile.accessMetres < current.accessMetres;
  }
  if (profile.longestConnectedMainlineMetres !== current.longestConnectedMainlineMetres) {
    return profile.longestConnectedMainlineMetres > current.longestConnectedMainlineMetres;
  }
  return route.distance < currentRoute.distance;
}

/**
 * Absolute default-route priority for a genuine expressway.
 *
 * This deliberately does not use BIG_CLASSES or maxspeed annotations. Trunk
 * and primary roads are ordinary dual carriageways with lights and junctions;
 * they are not substitutes for an elevated expressway.
 *
 * It honours a 15 km distance ceiling so an expressway cannot justify an
 * unbounded loop, but it does not use route ETA, traffic delay, or the driver's
 * maximum speed settings. Those estimates must not turn a twenty-kilometre
 * surface-road tail into the initial route.
 *
 * The comparison is deliberately lexicographic rather than weighted. First
 * avoid leaving and rejoining the expressway, then minimise the combined
 * surface distance before entry and after exit. The latter is symmetric when
 * the journey is reversed; the final tail breaks a true tie toward the exit
 * closest to the goal.
 */
export function preferredExpresswayIndex(
  routes: Route[],
  /** Kept for call-site compatibility; ETA no longer participates in selection. */
  _durationOf?: (route: Route) => number,
): number {
  if (!routes.length) return -1;
  const viable = new Set(viableExpresswayRoutes(routes));

  let best = -1;
  let bestProfile: ExpresswayGeometryProfile | null = null;

  for (let i = 0; i < routes.length; i++) {
    if (!viable.has(routes[i])) continue;
    const profile = expresswayGeometryProfile(routes[i]);
    if (!profile) continue;
    const wins =
      bestProfile === null ||
      expresswayGeometryWins(routes[i], profile, routes[best], bestProfile);
    if (wins) {
      best = i;
      bestProfile = profile;
    }
  }

  return best;
}

function closeExpresswayMeasure(a: number, b: number, floor: number, fraction: number): boolean {
  return Math.abs(a - b) <= Math.max(floor, Math.max(a, b) * fraction);
}

/**
 * Let live Mapbox traffic break a genuine expressway-geometry tie.
 *
 * This returns only a route that differs from the normal expressway winner;
 * `-1` means geometry remains decisive. Entry, exit, continuity and motorway
 * coverage must be close, and the candidate must save at least three live
 * minutes while shedding at least two minutes of live-vs-typical delay.
 */
export function preferredTrafficExpresswayIndex(routes: Route[]): number {
  const geometryChoice = preferredExpresswayIndex(routes);
  if (geometryChoice < 0) return -1;
  const base = routes[geometryChoice];
  const baseProfile = expresswayGeometryProfile(base);
  if (!baseProfile || !(base.duration_typical && base.duration > 0)) return -1;

  const viable = new Set(viableExpresswayRoutes(routes));
  const baseWidth = roadWidthProfile(base);
  const baseDelay = Math.max(0, base.duration - base.duration_typical);
  let best = geometryChoice;
  let bestDuration = base.duration;

  for (let index = 0; index < routes.length; index++) {
    const candidate = routes[index];
    const profile = expresswayGeometryProfile(candidate);
    if (
      index === geometryChoice ||
      !viable.has(candidate) ||
      !profile ||
      !(candidate.duration_typical && candidate.duration > 0) ||
      !closeExpresswayMeasure(candidate.distance, base.distance, 5_000, 0.08) ||
      Math.abs(profile.disconnectedSurfaceMetres - baseProfile.disconnectedSurfaceMetres) > 500 ||
      Math.abs(profile.accessMetres - baseProfile.accessMetres) > 1_500 ||
      Math.abs(profile.tailMetres - baseProfile.tailMetres) > 1_500 ||
      !closeExpresswayMeasure(
        profile.longestConnectedMainlineMetres,
        baseProfile.longestConnectedMainlineMetres,
        2_500,
        0.1,
      ) ||
      Math.abs(roadWidthProfile(candidate).motorwayFraction - baseWidth.motorwayFraction) > 0.08
    ) {
      continue;
    }
    const delay = Math.max(0, candidate.duration - candidate.duration_typical);
    if (baseDelay - delay < 120 || candidate.duration >= bestDuration) continue;
    best = index;
    bestDuration = candidate.duration;
  }

  return best !== geometryChoice && base.duration - bestDuration >= 180 ? best : -1;
}

/**
 * Seconds on a higher-speed road.
 *
 * Thai maxspeed annotations are commonly unknown, so road class is the stable
 * fallback. When Mapbox does provide segment limits, 80+ km/h segments count
 * too, even if the street class is incomplete.
 */
export function fastRoadSeconds(route: Route): number {
  let classified = 0;
  let annotated = 0;
  for (const leg of route.legs) {
    for (const step of leg.steps) {
      if (BIG_CLASSES.has(stepClass(step))) classified += step.duration;
    }

    const limits = leg.annotation?.maxspeed;
    const durations = leg.annotation?.duration;
    if (!limits || !durations) continue;
    const count = Math.min(limits.length, durations.length);
    for (let i = 0; i < count; i++) {
      const limit = limits[i];
      const speed = limit?.speed;
      if (typeof speed !== 'number' || !Number.isFinite(speed)) continue;
      const kmh = limit?.unit === 'mph' ? speed * 1.609344 : speed;
      if (kmh >= 80) annotated += durations[i] ?? 0;
    }
  }
  return Math.max(classified, annotated);
}

/** Maximum extra ETA accepted for a calmer higher-speed-road route. */
export const FAST_ROAD_DETOUR_S = 15 * 60;
/** Maximum extra distance accepted when that detour stays on faster roads. */
export const FAST_ROAD_EXTRA_DISTANCE_M = 15_000;
/** Minimum reduction in time spent on lower-speed roads before preferring it. */
export const FAST_ROAD_GAIN_S = 5 * 60;

/**
 * Which route should lead when the driver prefers 90 km/h roads over a 50 km/h
 * traffic crawl. Durations may be Mapbox ETAs or the driver's pace-adjusted
 * ETAs; step times are scaled to the same total before comparing surface time.
 */
export function preferredFastRoadIndex(
  routes: Route[],
  durationOf: (route: Route) => number = (route) => route.duration,
): number {
  if (!routes.length) return -1;
  let fastest = 0;
  let shortest = 0;
  for (let i = 1; i < routes.length; i++) {
    if (durationOf(routes[i]) < durationOf(routes[fastest])) fastest = i;
    if (routes[i].distance < routes[shortest].distance) shortest = i;
  }

  const fastestDuration = durationOf(routes[fastest]);
  const fastestTrafficDelay = routes[fastest].duration_typical
    ? Math.max(0, routes[fastest].duration - routes[fastest].duration_typical!)
    : 0;
  const surfaceTime = (route: Route): number => {
    const scale = route.duration > 0 ? durationOf(route) / route.duration : 1;
    return Math.max(0, route.duration - fastRoadSeconds(route)) * scale;
  };
  const fastestSurface = surfaceTime(routes[fastest]);

  let pick = fastest;
  let leastSurface = fastestSurface;
  for (let i = 0; i < routes.length; i++) {
    if (durationOf(routes[i]) > fastestDuration + FAST_ROAD_DETOUR_S) continue;
    if (routes[i].distance > routes[shortest].distance + FAST_ROAD_EXTRA_DISTANCE_M) continue;
    if (fastRoadSeconds(routes[i]) <= 0) continue;
    const trafficDelay = routes[i].duration_typical
      ? Math.max(0, routes[i].duration - routes[i].duration_typical!)
      : 0;
    // Never prefer a slower high-class road when its own live traffic is
    // materially worse. The preference is for free-flowing 90 over crawling
    // 50, not for sitting in a prettier queue.
    if (durationOf(routes[i]) >= fastestDuration && trafficDelay > fastestTrafficDelay + 180) continue;
    const surface = surfaceTime(routes[i]);
    if (surface < leastSurface) {
      pick = i;
      leastSurface = surface;
    }
  }
  return fastestSurface - leastSurface >= FAST_ROAD_GAIN_S ? pick : fastest;
}

/** Does this route genuinely stay on big roads? */
export function isMostlyBig(p: RoadWidthProfile): boolean {
  return p.bigFraction >= MOSTLY_BIG && p.worstSmallRun <= MAX_SMALL_RUN_M;
}

/**
 * Rank for "biggest roads". Higher is better.
 *
 * The share leads, but a long run off the big roads is penalised directly, so
 * a route cannot average its way to the top while hiding a bad section. The
 * national-highway share breaks ties towards the road that is actually divided
 * rather than merely classified as primary.
 */
export function widthScore(p: RoadWidthProfile): number {
  const runPenalty = Math.min(0.35, p.worstSmallRun / 100_000);
  // Motorway breaks ties hardest - it is the only tier with no lights and no
  // junctions - with plain dual carriageway a smaller bonus over the rest.
  return p.bigFraction + p.motorwayFraction * 0.2 + p.dualFraction * 0.08 - runPenalty;
}

/**
 * Index of the best big-road route in a candidate pool, or -1 when empty.
 *
 * Never returns "none suitable": the driver asked for the option to exist
 * always, so the biggest available roads win even when the result is mediocre.
 * The interface states the actual percentage rather than implying a guarantee.
 */
export function bestBigRoadIndex(profiles: RoadWidthProfile[]): number {
  let best = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < profiles.length; i++) {
    const score = widthScore(profiles[i]);
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}
